import React from 'react';
import { APP_COLORS as C, APP_FONT as F } from '../tokens';
import { fmtMoney, fmtPct } from '../helpers';
import type { ProfitLoss } from '../types';

type Props = {
  data: ProfitLoss;
  onViewDetail?: () => void;
  onHelpful?: (helpful: boolean) => void;
};

const Thumb = ({ down, onClick }: { down?: boolean; onClick?: () => void }) => {
  const [hovered, setHovered] = React.useState(false);
  return (
    <span
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={onClick}
      style={{
        width: 26,
        height: 26,
        borderRadius: '50%',
        border: '1px solid ' + (hovered ? C.teal : C.line),
        background: hovered ? C.teal : '#fff',
        display: 'grid',
        placeItems: 'center',
        cursor: 'pointer',
      }}
    >
      <svg
        viewBox="0 0 24 24"
        width={13}
        height={13}
        fill="none"
        stroke={hovered ? '#fff' : C.sub}
        strokeWidth={2.2}
        style={down ? { transform: 'scaleY(-1)' } : undefined}
      >
        <path d="M7 10v12H3V10z" />
        <path d="M7 10l5-8c2 0 3 1 3 3v3h5a2 2 0 012 2l-2 10a2 2 0 01-2 2H7" />
      </svg>
    </span>
  );
};

export default function ProfitLossCard({ data, onViewDetail, onHelpful }: Props) {
  const incomeMax = Math.max(data.income, data.expenses, 1);
  const incomePct = Math.round((data.income / incomeMax) * 100);
  const expensePct = Math.round((data.expenses / incomeMax) * 100);
  const isUp = data.deltaPct >= 0;

  return (
    <div style={{
      background: C.card,
      border: '1px solid ' + C.line,
      borderRadius: 14,
      padding: '20px 22px',
      display: 'flex',
      flexDirection: 'column',
      fontFamily: F.family,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', color: C.sub, textTransform: 'uppercase' }}>
          Profit &amp; Loss
        </span>
        <span style={{ fontSize: 12, color: C.sub, display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
          {data.periodLabel}
          <svg viewBox="0 0 24 24" width={12} height={12} fill="none" stroke={C.sub} strokeWidth={2.2}>
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </span>
      </div>

      <p style={{ fontSize: 12.5, color: C.sub, margin: '0 0 6px' }}>Net profit</p>
      <div style={{
        fontSize: 28,
        fontWeight: 800,
        color: data.netProfit < 0 ? C.red : C.ink,
        letterSpacing: '-0.015em',
        fontVariantNumeric: 'tabular-nums',
        marginBottom: 4,
      }}>
        {fmtMoney(data.netProfit)}
      </div>
      <div style={{
        fontSize: 12,
        marginBottom: 14,
        color: isUp ? C.green : C.red,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
      }}>
        <svg viewBox="0 0 24 24" width={12} height={12} fill="none" stroke="currentColor" strokeWidth={2.5}>
          <polyline points={isUp ? '18 15 12 9 6 15' : '6 9 12 15 18 9'} />
        </svg>
        {fmtPct(data.deltaPct)} vs previous period
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12.5, marginBottom: 8 }}>
        <span style={{ width: 70, color: C.sub, fontWeight: 600 }}>Income</span>
        <div style={{ flex: 1, height: 10, background: C.lineSoft, borderRadius: 999, overflow: 'hidden' }}>
          <div style={{ height: '100%', borderRadius: 999, background: C.teal, width: incomePct + '%' }} />
        </div>
        <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: C.ink, minWidth: 92, textAlign: 'right', fontSize: 13 }}>
          {fmtMoney(data.income)}
        </span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12.5 }}>
        <span style={{ width: 70, color: C.sub, fontWeight: 600 }}>Expenses</span>
        <div style={{ flex: 1, height: 10, background: C.lineSoft, borderRadius: 999, overflow: 'hidden' }}>
          <div style={{ height: '100%', borderRadius: 999, background: C.ink, width: expensePct + '%' }} />
        </div>
        <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: C.ink, minWidth: 92, textAlign: 'right', fontSize: 13 }}>
          {fmtMoney(data.expenses)}
        </span>
      </div>

      <div style={{ marginTop: 'auto', paddingTop: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span onClick={onViewDetail} style={{ fontSize: 12.5, color: C.tealInk, fontWeight: 700, cursor: 'pointer' }}>
          View P&amp;L →
        </span>
        <span style={{ fontSize: 11, color: C.sub, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          Helpful?
          <span style={{ display: 'inline-flex', gap: 4 }}>
            <Thumb onClick={() => onHelpful?.(true)} />
            <Thumb down onClick={() => onHelpful?.(false)} />
          </span>
        </span>
      </div>
    </div>
  );
}
